import { useState } from 'react';
import { PhoneOverride, OverrideType } from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { LocationsApi } from '@frontend/api-locations';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useHasPhoneSettingsAccess, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Button,
  Chip,
  Modal,
  ModalControlModalProps,
  PopoverMenu,
  PopoverMenuItem,
  Table,
  Text,
  TextLink,
  useModalControl,
  usePopoverMenu,
} from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { useOverridesManagementPermission } from '../../userManagementPermission';
import { NewOverrideSettingTray } from './override-settings-modal.new';
import { NewOverrideSettingRemoveModal } from './override-settings-remove-modal.new';
import { PhoneOverrideType } from './types';

dayjs.extend(timezone);

export interface ActiveOverridesModalProps {
  /** The modal props from useModalControl */
  modalProps: ModalControlModalProps;
  /** The title of the modal */
  title?: string;
  /** Array of overrides to display in the table */
  overrides: PhoneOverrideType[];
  /** Optional callback for primary action button click. Overrides default behavior */
  onPrimaryActionClick?: () => void;
}

/**
 * Helper function to get a human-readable override type label
 */
export const getOverrideTypeLabel = (override: PhoneOverride, t: (key: string) => string): string => {
  const { overrideType } = override;

  switch (overrideType) {
    case OverrideType.OVERRIDE_TYPE_VMO:
      return t('Send to Voicemail');
    case OverrideType.OVERRIDE_TYPE_FWD:
      return t('Forward Call');
    case OverrideType.OVERRIDE_TYPE_IVR:
      return t('Send to Phone Tree');
    default:
      return '--';
  }
};

/**
 * Helper function to format the override end time
 */
export const getOverrideEndTime = (override: PhoneOverride | undefined, officeTimezone: string | undefined): string => {
  if (override && override.endAt) {
    return dayjs(override.endAt).tz(officeTimezone).format('MMM D YYYY h:mma z');
  }
  return '--';
};

export const ActiveOverridesModal = ({
  modalProps,
  title,
  overrides,
  onPrimaryActionClick,
}: ActiveOverridesModalProps) => {
  return (
    <ActiveOverrides
      modalProps={modalProps}
      title={title}
      closeModal={modalProps.onClose}
      overrides={overrides}
      onPrimaryActionClick={onPrimaryActionClick}
    />
  );
};

/**
 * A reusable modal component that displays a table of active overrides
 * with actions to edit or remove them.
 */
const ActiveOverrides = ({
  modalProps,
  title,
  closeModal,
  overrides,
  onPrimaryActionClick,
}: ActiveOverridesModalProps & {
  closeModal: () => void;
}) => {
  const { t } = useTranslation('phone');
  const { getLocationName, selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const { navigate } = useSettingsNavigate();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const hasOverridesManagementPermission = useOverridesManagementPermission();
  const phoneAccess = useHasPhoneSettingsAccess({
    selectedLocations: selectedLocationIds?.map((id) => accessibleLocationData[id]).filter(Boolean) ?? [],
  });

  const enableCallRouteLinks = phoneAccess.hasFullPhoneAccess;

  const removeOverrideModal = useModalControl();
  const trayModalControl = useModalControl();
  const [selectedOverride, setSelectedOverride] = useState<PhoneOverrideType>();

  const { data: officeTimezone } = useScopedQuery({
    queryKey: queryKeys.timezone(selectedLocationIds[0]),
    queryFn: () => LocationsApi.getOfficeTimezone({}),
    select: (data) => data.timezone,
  });

  const handleEditOverride = (override: PhoneOverrideType) => {
    setSelectedOverride(override);
    trayModalControl.openModal();
  };

  const handleOpenRemoveModal = (override: PhoneOverrideType) => {
    setSelectedOverride(override);
    removeOverrideModal.openModal();
  };

  const handleNavigateToCallRoute = (callRouteId: string) => {
    navigate({
      to: '/phone/call-routes/:id',
      params: { id: callRouteId },
    });
  };

  const isSingleOverride = overrides.length === 1;

  return (
    <>
      <Modal {...modalProps} minWidth={800}>
        <Modal.Header
          onClose={closeModal}
          closeBtnTrackingId='phone-portal-weave2.0-override-notification-btn-closeModal'
        >
          {title || t('Overrides')}
        </Modal.Header>
        <Modal.Body>
          <Text>{t('Call Routes with active Overrides are unable to receive calls.')}</Text>
          <Table
            colConfig={[
              {
                Header: t('Call Route'),
                accessor: (override) =>
                  enableCallRouteLinks ? (
                    <TextLink
                      onClick={() => {
                        closeModal();
                        handleNavigateToCallRoute(override.callRouteId ?? '');
                      }}
                    >
                      {override.callRouteName}
                    </TextLink>
                  ) : (
                    <Text size='medium'>{override.callRouteName}</Text>
                  ),
                id: 'name',
                width: 100,
              },
              {
                Header: t('Override Type'),
                id: 'override type',
                accessor: (override) => getOverrideTypeLabel(override, t),
                width: 100,
              },
              {
                Header: t('Override Ends'),
                id: 'end time',
                accessor: (override) => getOverrideEndTime(override, officeTimezone),
                width: 100,
              },
              {
                Header: t('Location'),
                accessor: (override) => (
                  <Chip.Location variant='primary'>{getLocationName(override.locationId ?? '')}</Chip.Location>
                ),
                id: 'location',
                width: 100,
                omit: selectedLocationIds.length === 1,
              },
              {
                Header: '',
                id: 'viewAction',
                disableSortBy: true,
                maxWidth: 10,
                accessor: (data) => data,
                cellRenderer: (row) => (
                  <OverrideRowActions
                    row={row}
                    hasPermission={hasOverridesManagementPermission(row.locationId ?? '')}
                    onEdit={handleEditOverride}
                    onRemove={handleOpenRemoveModal}
                    t={t}
                  />
                ),
              },
            ]}
            hasResponsiveColWidths
            data={overrides}
          />
          <div
            css={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(3), marginTop: theme.spacing(3) }}
          >
            <TextLink
              weight='bold'
              onClick={() => {
                if (onPrimaryActionClick) {
                  onPrimaryActionClick();
                  return;
                }

                if (isSingleOverride) {
                  setSelectedOverride(overrides[0]);
                  trayModalControl.openModal();
                } else {
                  navigate({ to: '/phone/overrides' });
                  closeModal();
                }
              }}
              trackingId='phone-portal-weave2.0-override-notification-btn-manageInSettings'
            >
              {t('Go to Overrides')}
            </TextLink>
            <Button
              onClick={closeModal}
              variant='secondary'
              trackingId='phone-portal-weave2.0-override-notification-btn-closeModal'
            >
              {t('Close')}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <NewOverrideSettingRemoveModal
        override={selectedOverride ?? ({} as PhoneOverrideType)}
        modalProps={removeOverrideModal.modalProps}
        phoneTenantId={settingsTenantLocation?.phoneTenantId ?? ''}
      />
      <NewOverrideSettingTray
        override={selectedOverride}
        locationId={selectedLocationIds[0]}
        modalControls={trayModalControl}
      />
    </>
  );
};

/**
 * Row actions component - each row needs its own usePopoverMenu hook instance
 * to ensure the correct row data is captured when menu items are clicked.
 */
const OverrideRowActions = ({
  row,
  hasPermission,
  onEdit,
  onRemove,
  t,
}: {
  row: PhoneOverrideType;
  hasPermission: boolean;
  onEdit: (override: PhoneOverrideType) => void;
  onRemove: (override: PhoneOverrideType) => void;
  t: (key: string) => string;
}) => {
  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu({ placement: 'left' });

  if (!hasPermission) {
    return (
      <Button
        iconName='view'
        size='large'
        variant='secondary'
        onClick={() => onEdit(row)}
        trackingId='phone-portal-weave2.0-override-notification-btn-view'
      />
    );
  }

  return (
    <div>
      <Button iconName='more' label={t('Open Menu')} size='large' variant='secondary' {...getTriggerProps()} />
      <PopoverMenu {...getMenuProps()}>
        <PopoverMenuItem
          {...getItemProps}
          Icon={() => <Icon name='edit-small' />}
          onClick={() => {
            close();
            onEdit(row);
          }}
          trackingId='phone-portal-weave2.0-override-notification-btn-edit'
        >
          <Text as='span'>{t('Edit Override')}</Text>
        </PopoverMenuItem>
        <PopoverMenuItem
          {...getItemProps}
          Icon={() => <Icon name='trash-small' color='error' />}
          onClick={() => {
            close();
            onRemove(row);
          }}
          trackingId='phone-portal-weave2.0-override-notification-btn-remove'
        >
          <Text as='span'>{t('Remove Override')}</Text>
        </PopoverMenuItem>
      </PopoverMenu>
    </div>
  );
};
